import Modal from "@via/components/modal.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import {
  useRemovePipelineFromProject,
  useDeletePipeline,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import { useNavigate, useParams } from "react-router-dom";

const DeletePipelineModal = ({
  open,
  setOpen,
  pipelineName,
  pipelineId,
  pipelineVersion = "",
}) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const {
    mutateAsync: removePipelineFromProject,
    isPending: isRemovePipelineFromProjectLoading,
  } = useRemovePipelineFromProject();
  const { resetChangeCount } = useChangesDetect();

  const { mutateAsync: deletePipeline, isPending: isDeletePipelineLoading } =
    useDeletePipeline();

  // Two mutations reach the same close, so awaiting whichever one ran is what tells
  // the dialog it is done. Resolving hands the close to Modal and a rejection leaves
  // it open with that mutation's own error on screen, where the success effect used
  // to close on either flag regardless of which call had been made.
  //
  // resetChangeCount now runs only once the removal has actually succeeded. It used
  // to fire the moment the request was sent, so a failure cleared the unsaved
  // changes marker on an editor whose changes were still unsaved.
  const handleConfirmModal = async (): Promise<void> => {
    if (pipelineId && projectId) {
      await removePipelineFromProject({ projectId, pipelineId });
    } else {
      await deletePipeline(pipelineId);
    }
    resetChangeCount();
    navigate(projectId ? `/projects/${projectId}/pipelines` : "/pipelines");
  };

  // This is extra insurance that modal data is destroyed
  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={
        projectId ? "Remove Pipeline from Project" : "Delete Pipeline Revision"
      }
      confirmText="Confirm"
      size={ModalSize.Large}
      onCancel={handleCancel}
      isConfirmLoading={
        isRemovePipelineFromProjectLoading || isDeletePipelineLoading
      }
      onConfirm={handleConfirmModal}>
      <form>
        <div className="mt-0">
          <div className="mb-4 text-sm text-gray-600 max-w-[600px]">
            {projectId ? (
              <>
                Are you sure you want to remove the pipeline named{" "}
                <span className="font-semibold">{pipelineName}</span> from the
                project pipeline list?
              </>
            ) : (
              <>
                Are you sure you want to permanently delete the selected
                revision{" "}
                <span className="font-semibold">({pipelineVersion})</span> of
                the pipeline?
              </>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default DeletePipelineModal;
